/**
 * Single-session enforcement (FR-66) — client-side session id storage.
 *
 * The id is generated once per actual sign-in (or force-takeover) and
 * persists across page refreshes via localStorage so the "same browser
 * returning" path stays silent (no takeover prompt every time the user
 * reloads). Cleared on clean sign-out or when the server tells us
 * `session_revoked` (kicked from elsewhere).
 */

const KEY = 'lumi:sessionId';

export function getStoredSessionId(): string | undefined {
  try {
    return window.localStorage.getItem(KEY) ?? undefined;
  } catch {
    // Safari private-mode etc. — fall back to in-memory only.
    return undefined;
  }
}

export function storeSessionId(sessionId: string): void {
  try {
    window.localStorage.setItem(KEY, sessionId);
  } catch {
    // Storage quota / disabled — non-fatal.
  }
}

export function clearStoredSessionId(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Ignore.
  }
}

/**
 * Generate a fresh UUID. Uses `crypto.randomUUID` where available (all
 * modern browsers); falls back to a Math.random-derived variant for the
 * unlikely older-Safari case (still adequate as a session opaque id —
 * not a security token).
 */
export function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122 v4-ish fallback. Good enough for an opaque id.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
